// Property show page Alpine.js components
// Registered globally so they work with Turbo Frame navigation

/**
 * Property Enquiries component
 * Displays enquiries table with real-time updates via Action Cable
 *
 * Usage: x-data="propertyEnquiries" data-property-id="123" data-enquiries="[...]"
 */
export function propertyEnquiries() {
  return {
    propertyId: null,
    enquiries: [],
    showEnquiryModal: false,
    showReplyModal: false,
    selectedEnquiry: null,

    init() {
      // Get property ID from data attribute
      this.propertyId = parseInt(this.$el.dataset.propertyId)

      // Parse initial enquiries from data attribute
      const initialData = this.$el.dataset.enquiries
      if (initialData) {
        try {
          this.enquiries = JSON.parse(initialData)
        } catch (e) {
          console.error('Failed to parse enquiries data:', e)
        }
      }
    },

    handleEnquiryUpdate(message) {
      switch(message.action) {
        case 'update':
          const index = this.enquiries.findIndex(e => e.id === message.id)
          if (index !== -1) {
            this.enquiries[index] = message.data
          }
          break

        case 'create':
          // Add to beginning of list
          this.enquiries.unshift(message.data)
          // Keep only the 10 most recent
          if (this.enquiries.length > 10) {
            this.enquiries = this.enquiries.slice(0, 10)
          }
          break

        case 'destroy':
          this.enquiries = this.enquiries.filter(e => e.id !== message.id)
          break
      }
    },

    viewEnquiry(enquiryId) {
      fetch(`/enquiries/${enquiryId}.json`, {
        headers: { 'Accept': 'application/json' }
      })
      .then(response => response.json())
      .then(enquiry => {
        this.selectedEnquiry = enquiry
        this.showEnquiryModal = true
      })
      .catch(error => {
        console.error('Error loading enquiry:', error)
        alert('Failed to load enquiry details')
      })
    },

    openReplyModal(enquiryId) {
      // TODO: Implement reply modal when autoresponder is built
      alert('Reply functionality coming soon')
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },

    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }) + ' at ' + date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // Clean comments to remove pipe-delimited enhanced data
    cleanComments(comments) {
      if (!comments) return ''

      // The enhanced data appears after the actual message, separated by pipes
      // Example: "Real message\n|MONTH|TWELVE_MONTHS|false..."
      const pipeIndex = comments.indexOf('\n|')
      if (pipeIndex !== -1) {
        return comments.substring(0, pipeIndex).trim()
      }

      return comments.trim()
    },

    // Check if enquiry has enhanced data to display
    hasEnhancedData(enquiry) {
      return enquiry?.additional_data &&
             Object.keys(enquiry.additional_data).some(key =>
               key !== 'raw_email_body' && enquiry.additional_data[key]
             )
    },

    // Get enhanced field value from additional_data or parse from comments
    getEnhancedField(enquiry, fieldName) {
      // First try additional_data
      if (enquiry?.additional_data?.[fieldName]) {
        return enquiry.additional_data[fieldName]
      }

      // Fallback: parse from pipe-delimited comments if needed
      // This handles legacy data or cases where extraction failed
      return null
    }
  }
}

/**
 * Property Keys component
 * Displays key bunches with checkout/check-in functionality
 *
 * Usage: x-data="propertyKeys" data-property-id="123" data-property-address="..." data-key-bunches="[...]"
 */
export function propertyKeys() {
  return {
    propertyId: null,
    propertyAddress: '',
    keyBunches: [],
    showAddKeyForm: false,
    showEditKeyForm: false,
    showCheckoutForm: false,
    showHistoryModal: false,
    selectedKeyBunchId: null,
    selectedPropertyAddress: '',
    checkoutPropertyAddress: '',
    currentKeyStatus: '',
    currentKeyStatusText: '',
    selectedKeyIdentifier: '',
    hasCheckedOutKeys: false,
    showPermanentCheckoutWarning: false,
    showPeopleDropdown: false,
    peopleResults: [],
    historyLogs: [],
    loadingHistory: false,
    newKeyBunch: {
      identifier: '',
      notes: ''
    },
    editKeyBunch: {
      id: null,
      identifier: '',
      notes: ''
    },
    newCheckout: {
      checked_out_to: '',
      contact_email: '',
      contact_phone: '',
      expected_return_at: ''
    },
    permanentCheckout: {
      given_to: '',
      notes: ''
    },
    identifierValidation: {
      checking: false,
      error: null
    },

    init() {
      // Get property ID and address from data attributes
      this.propertyId = parseInt(this.$el.dataset.propertyId)
      this.propertyAddress = this.$el.dataset.propertyAddress || ''
      this.checkoutPropertyAddress = this.propertyAddress

      // Parse initial key bunches from data attribute
      const initialData = this.$el.dataset.keyBunches
      if (initialData) {
        try {
          this.keyBunches = JSON.parse(initialData)
        } catch (e) {
          console.error('Failed to parse key bunches data:', e)
        }
      }
    },

    handleKeyBunchUpdate(message) {
      switch(message.action) {
        case 'update':
          const index = this.keyBunches.findIndex(k => k.id === message.id)
          if (index !== -1) {
            this.keyBunches[index] = message.data
          }
          break

        case 'create':
          this.keyBunches.push(message.data)
          break

        case 'destroy':
          this.keyBunches = this.keyBunches.filter(k => k.id !== message.id)
          break
      }
    },

    async saveKeyBunch() {
      // Don't allow save if there's a validation error
      if (this.identifierValidation.error) {
        return
      }

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            property_key_bunch: this.newKeyBunch
          })
        })

        if (response.ok) {
          this.showAddKeyForm = false
          this.newKeyBunch = { identifier: '', notes: '' }
          this.identifierValidation = { checking: false, error: null }
        } else {
          // Handle server-side validation errors
          const data = await response.json()
          if (data.errors && data.errors.length > 0) {
            this.identifierValidation.error = data.errors.join(', ')
          } else {
            alert('Error saving key bunch')
          }
        }
      } catch (error) {
        console.error('Error saving key bunch:', error)
        alert('Error saving key bunch')
      }
    },

    openEditKeyForm(keyBunch) {
      this.editKeyBunch = {
        id: keyBunch.id,
        identifier: keyBunch.identifier,
        notes: keyBunch.notes || '',
        propertyId: this.propertyId,
        propertyAddress: this.propertyAddress
      }
      this.showEditKeyForm = true
    },

    async updateKeyBunch() {
      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.editKeyBunch.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            property_key_bunch: {
              identifier: this.editKeyBunch.identifier,
              notes: this.editKeyBunch.notes
            }
          })
        })

        if (response.ok) {
          this.showEditKeyForm = false
          this.editKeyBunch = { id: null, identifier: '', notes: '' }
        }
      } catch (error) {
        console.error('Error updating key bunch:', error)
        alert('Error updating key bunch')
      }
    },

    async saveCheckout() {
      try {
        const keyBunchId = this.selectedKeyBunchId
        const response = await fetch(`/properties/${this.propertyId}/keys/${keyBunchId}/checkouts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            property_key_checkout: this.newCheckout
          })
        })

        if (response.ok) {
          this.showCheckoutForm = false
          this.selectedKeyBunchId = null
          this.newCheckout = { checked_out_to: '', contact_email: '', contact_phone: '', expected_return_at: '' }
          this.peopleResults = []
        }
      } catch (error) {
        console.error('Error checking out keys:', error)
        alert('Error checking out keys')
      }
    },

    async checkIdentifier() {
      const identifier = this.newKeyBunch.identifier.trim()

      // Reset validation if identifier is empty
      if (!identifier) {
        this.identifierValidation = { checking: false, error: null }
        return
      }

      this.identifierValidation.checking = true
      this.identifierValidation.error = null

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/check_identifier?identifier=${encodeURIComponent(identifier)}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          }
        })

        const data = await response.json()

        if (!data.available) {
          this.identifierValidation.error = data.message
        }
      } catch (error) {
        console.error('Error checking identifier:', error)
      } finally {
        this.identifierValidation.checking = false
      }
    },

    async searchPeople() {
      // Determine which field is being used (regular checkout or permanent checkout)
      const query = (this.showPermanentCheckoutWarning
        ? this.permanentCheckout.given_to
        : this.newCheckout.checked_out_to).trim()

      if (query.length < 2) {
        this.peopleResults = []
        return
      }

      try {
        const response = await fetch(`/api/people/search?q=${encodeURIComponent(query)}`, {
          headers: {
            'Accept': 'application/json'
          }
        })

        if (response.ok) {
          this.peopleResults = await response.json()
          this.showPeopleDropdown = true
        }
      } catch (error) {
        console.error('Error searching people:', error)
      }
    },

    selectPerson(person) {
      this.newCheckout.checked_out_to = person.name
      this.newCheckout.contact_email = person.email || ''
      this.newCheckout.contact_phone = person.phone || ''
      this.peopleResults = []
      this.showPeopleDropdown = false
    },

    selectPersonForPermanent(person) {
      this.permanentCheckout.given_to = person.name
      this.peopleResults = []
      this.showPeopleDropdown = false
    },

    async checkInKeys(keyBunchId) {
      try {
        const keyBunch = this.keyBunches.find(k => k.id === keyBunchId)
        const checkoutId = keyBunch.current_checkout.id

        const response = await fetch(`/properties/${this.propertyId}/keys/${keyBunchId}/checkouts/${checkoutId}/check_in`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          }
        })

        if (!response.ok) {
          alert('Error checking in keys')
        }
      } catch (error) {
        console.error('Error checking in keys:', error)
        alert('Error checking in keys')
      }
    },

    async confirmPermanentCheckout() {
      try {
        const keyBunchId = this.selectedKeyBunchId
        const response = await fetch(`/properties/${this.propertyId}/keys/${keyBunchId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            given_to: this.permanentCheckout.given_to,
            notes: this.permanentCheckout.notes
          })
        })

        if (response.ok) {
          this.showPermanentCheckoutWarning = false
          this.selectedKeyBunchId = null
          this.permanentCheckout = { given_to: '', notes: '' }
        } else {
          alert('Error checking out keys permanently')
        }
      } catch (error) {
        console.error('Error checking out keys permanently:', error)
        alert('Error checking out keys permanently')
      }
    },

    async loadKeyHistory(keyBunchId) {
      this.loadingHistory = true
      this.showHistoryModal = true
      this.historyLogs = []
      this.selectedPropertyAddress = this.propertyAddress

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${keyBunchId}/history`, {
          headers: {
            'Accept': 'application/json'
          }
        })

        if (response.ok) {
          this.historyLogs = await response.json()
        } else {
          console.error('Error loading history')
          alert('Error loading checkout history')
        }
      } catch (error) {
        console.error('Error loading history:', error)
        alert('Error loading checkout history')
      } finally {
        this.loadingHistory = false
      }
    },

    async loadAllPropertyKeyLogs() {
      this.loadingHistory = true
      this.showHistoryModal = true
      this.historyLogs = []
      this.selectedPropertyAddress = this.propertyAddress

      // Build current key status message
      if (this.keyBunches.length === 0) {
        this.currentKeyStatus = ''
        this.currentKeyStatusText = 'No keys currently on site'
        this.selectedKeyIdentifier = 'N/A'
        this.hasCheckedOutKeys = false
      } else {
        // Check if any keys are checked out
        this.hasCheckedOutKeys = this.keyBunches.some(bunch => bunch.current_checkout)

        // Set identifier for display (show first key or count)
        if (this.keyBunches.length === 1) {
          this.selectedKeyIdentifier = this.keyBunches[0].identifier
          if (this.keyBunches[0].current_checkout) {
            this.currentKeyStatusText = `Checked out to ${this.keyBunches[0].current_checkout.checked_out_to}`
          } else {
            this.currentKeyStatusText = 'Currently checked in'
          }
        } else {
          const checkedOutCount = this.keyBunches.filter(b => b.current_checkout).length
          const checkedInCount = this.keyBunches.length - checkedOutCount
          this.selectedKeyIdentifier = `${this.keyBunches.length} keys`
          this.currentKeyStatusText = `${checkedInCount} checked in, ${checkedOutCount} checked out`
        }
        this.currentKeyStatus = 'active'
      }

      try {
        const response = await fetch(`/properties/${this.propertyId}/key_logs`, {
          headers: {
            'Accept': 'application/json'
          }
        })

        if (response.ok) {
          this.historyLogs = await response.json()
        } else {
          console.error('Error loading key logs')
          alert('Error loading key logs')
        }
      } catch (error) {
        console.error('Error loading key logs:', error)
        alert('Error loading key logs')
      } finally {
        this.loadingHistory = false
      }
    },

    async confirmDeleteKey() {
      // Show confirmation dialog
      if (!confirm('Are you sure you want to delete this key permanently? This action cannot be reversed, but a log entry will be retained.')) {
        return
      }

      try {
        const response = await fetch(`/properties/${this.propertyId}/keys/${this.editKeyBunch.id}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          }
        })

        if (response.ok) {
          this.showEditKeyForm = false
          this.editKeyBunch = { id: null, identifier: '', notes: '' }
        } else {
          alert('Error deleting key bunch')
        }
      } catch (error) {
        console.error('Error deleting key bunch:', error)
        alert('Error deleting key bunch')
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}

/**
 * Property Inspections component
 * Displays inspections table with real-time updates via Action Cable
 *
 * Usage: x-data="propertyInspections" data-property-id="123" data-inspections="[...]"
 */
export function propertyInspections() {
  return {
    propertyId: null,
    inspections: [],

    init() {
      // Get property ID and initial data from data attributes
      this.propertyId = parseInt(this.$el.dataset.propertyId)

      // Parse initial inspections from data attribute
      const initialData = this.$el.dataset.inspections
      if (initialData) {
        try {
          const inspections = JSON.parse(initialData)
          this.inspections = inspections.map(i => this.transformInspection(i))
        } catch (e) {
          console.error('Failed to parse inspections data:', e)
        }
      }
    },

    // Transform raw inspection data into display format
    transformInspection(data) {
      return {
        id: data.id,
        uuid: data.uuid,
        status: data.status,
        status_display: this.formatStatus(data.status),
        status_class: this.getStatusClass(data.status),
        formatted_date: this.formatDate(data.scheduled_date || data.created_at),
        scheduled_date: data.scheduled_date,
        inspector_name: data.inspector ? data.inspector.full_name : '-',
        next_due_date: this.formatDate(data.next_inspection_due_date),
        show_url: `/inspections/${data.uuid}`,
        property: data.property
      }
    },

    // Handle granular update from Action Cable (update just one row)
    handleInspectionUpdate(message) {
      switch(message.action) {
        case 'update':
          // Find and update just this row
          const index = this.inspections.findIndex(i => i.id === message.id)
          if (index !== -1) {
            const updatedInspection = this.transformInspection(message.data)
            this.inspections[index] = updatedInspection
          }
          break

        case 'create':
          // New inspection for this property - add to list
          const newInspection = this.transformInspection(message.data)
          this.inspections.unshift(newInspection) // Add to beginning
          break

        case 'destroy':
          // Deleted inspection - remove from list
          this.inspections = this.inspections.filter(i => i.id !== message.id)
          break
      }
    },

    // Format status for display
    formatStatus(status) {
      const statusMap = {
        'booked': 'Booked',
        'draft': 'Draft',
        'in_progress': 'In Progress',
        'processing': 'Processing',
        'ready_to_send': 'Ready to Send',
        'sent': 'Sent',
        'completed': 'Completed'
      }
      return statusMap[status] || status
    },

    // Get CSS classes for status badge
    getStatusClass(status) {
      const classMap = {
        'booked': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        'draft': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
        'syncing': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        'submitted': 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
        'processing': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        'ready_to_send': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        'sent': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        'completed': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      }
      return classMap[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },

    // Delete inspection
    deleteInspection(inspectionId) {
      if (confirm('Are you sure you want to delete this inspection?')) {
        fetch(`/properties/${this.propertyId}/inspections/${inspectionId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'application/json'
          }
        })
        .then(response => {
          if (response.ok) {
            // Remove from local array (will also be removed via Action Cable broadcast)
            this.inspections = this.inspections.filter(i => i.id !== inspectionId)
          } else {
            alert('Failed to delete inspection')
          }
        })
        .catch(error => {
          console.error('Error:', error)
          alert('Failed to delete inspection')
        })
      }
    }
  }
}

/**
 * Property Meters component
 * Displays meter readings with tabs for gas/electric
 *
 * Usage: x-data="propertyMeters" data-property-id="123" data-meters="[...]"
 */
export function propertyMeters() {
  return {
    propertyId: null,
    selectedMeterType: 'gas',
    meters: [],
    showDropdown: false,

    init() {
      // Get property ID and initial data from data attributes
      this.propertyId = parseInt(this.$el.dataset.propertyId)

      // Parse initial meters from data attribute
      const initialData = this.$el.dataset.meters
      if (initialData) {
        try {
          this.meters = JSON.parse(initialData)
        } catch (e) {
          console.error('Failed to parse meters data:', e)
        }
      }
    },

    getMetersForType(type) {
      return this.meters.filter(m => m.meter_type === type)
    },

    // Handle meter reading updates from Action Cable
    handleMeterReadingUpdate(message) {
      switch(message.action) {
        case 'create':
          // New meter reading added - update the meter
          const meter = this.meters.find(m => m.id === message.data.meter.id)
          if (meter) {
            // Format the new reading
            const newReading = {
              id: message.data.id,
              reading_value: message.data.reading_value,
              reading_date: this.formatDate(message.data.reading_date),
              notes: message.data.notes
            }

            // Add to readings array at the beginning (most recent first)
            meter.readings.unshift(newReading)

            // Update latest_reading
            meter.latest_reading = {
              reading_value: message.data.reading_value,
              reading_date: this.formatDate(message.data.reading_date),
              notes: message.data.notes
            }
          }
          break

        case 'update':
          // Meter reading updated - find and update it
          const meterForUpdate = this.meters.find(m => m.id === message.data.meter.id)
          if (meterForUpdate) {
            const readingIndex = meterForUpdate.readings.findIndex(r => r.id === message.data.id)
            if (readingIndex !== -1) {
              meterForUpdate.readings[readingIndex] = {
                id: message.data.id,
                reading_value: message.data.reading_value,
                reading_date: this.formatDate(message.data.reading_date),
                notes: message.data.notes
              }
            }

            // Update latest_reading if this is the most recent
            if (meterForUpdate.readings[0]?.id === message.data.id) {
              meterForUpdate.latest_reading = {
                reading_value: message.data.reading_value,
                reading_date: this.formatDate(message.data.reading_date),
                notes: message.data.notes
              }
            }
          }
          break

        case 'destroy':
          // Meter reading deleted - remove from readings array
          const meterForDelete = this.meters.find(m => m.id === message.data.meter_id)
          if (meterForDelete) {
            meterForDelete.readings = meterForDelete.readings.filter(r => r.id !== message.id)
            // Update latest_reading to next most recent
            meterForDelete.latest_reading = meterForDelete.readings[0] || null
          }
          break
      }
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },

    // Open manage meters modal
    openManageMetersModal() {
      this.$store.modals.showManageMetersModal = true
      this.showDropdown = false
    },

    // Open add reading modal
    openAddReadingModal() {
      this.$store.modals.showAddReadingModal = true
      this.showDropdown = false
    },

    // Delete a meter reading
    async deleteReading(meterId, readingId) {
      if (!confirm('Are you sure you want to delete this reading?')) {
        return
      }

      try {
        const response = await fetch(`/properties/${this.propertyId}/meter-readings/${readingId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'application/json'
          }
        })

        if (!response.ok) {
          const data = await response.json()
          alert(data.errors?.join(', ') || 'Failed to delete reading')
        }
        // Success - ActionCable broadcast will update the UI automatically
      } catch (error) {
        console.error('Error deleting reading:', error)
        alert('Failed to delete reading')
      }
    },

    // Handle meter updates from Action Cable (for serial number, location changes etc)
    handleMeterUpdate(message) {
      switch(message.action) {
        case 'create':
          // New meter added - add it unless we already hold it (a local push
          // may have beaten the broadcast)
          if (!this.meters.some(m => m.id === message.data.id)) {
            this.meters.push(message.data)
          }
          break

        case 'update':
          // Meter updated (serial number, location, key_meter, etc)
          const meterIndex = this.meters.findIndex(m => m.id === message.data.id)
          if (meterIndex !== -1) {
            // Preserve readings if not included in broadcast, otherwise use broadcast data
            const existingReadings = this.meters[meterIndex].readings
            this.meters[meterIndex] = {
              ...message.data,
              readings: message.data.readings || existingReadings
            }
          }
          break

        case 'destroy':
          // Meter deleted - remove from array
          this.meters = this.meters.filter(m => m.id !== message.id)
          break
      }
    }
  }
}

/**
 * Property Certificates component
 * Displays certificates table with filtering and sorting
 *
 * Usage: x-data="propertyCertificates" data-property-id="123" data-certificates="[...]" data-epc="..."
 */
export function propertyCertificates() {
  return {
    propertyId: null,
    certificates: [],
    sortAsc: false, // Sort by expiry date descending by default (newest at top)
    filter: 'active', // Default to showing only active certificates

    init() {
      // Get property ID and initial data from data attributes
      this.propertyId = parseInt(this.$el.dataset.propertyId)

      // Parse initial certificates from data attribute
      const initialData = this.$el.dataset.certificates
      if (initialData) {
        try {
          const certificates = JSON.parse(initialData)
          this.certificates = certificates.map(c => this.transformCertificate(c))
        } catch (e) {
          console.error('Failed to parse certificates data:', e)
        }
      }

      // Add EPC if it exists
      const epcData = this.$el.dataset.epc
      if (epcData) {
        try {
          const epc = JSON.parse(epcData)
          this.certificates.unshift(this.transformEpc(epc))
        } catch (e) {
          console.error('Failed to parse EPC data:', e)
        }
      }
    },

    // Transform raw certificate data into display format
    transformCertificate(data) {
      return {
        id: data.id,
        property_id: data.property_id,
        certificate_type_id: data.certificate_type_id,
        certificate_type_name: data.certificate_type_name,
        certificate_type_slug: data.certificate_type_slug,
        certificate_type_color: data.certificate_type_color,
        certificate_number: data.certificate_number,
        issue_date: data.issue_date,
        expiry_date: data.expiry_date,
        formatted_expiry_date: this.formatDate(data.expiry_date),
        days_until_expiry: data.days_until_expiry,
        status_display: data.status_display,
        status_class: this.getStatusClass(data.status_display),
        contractor_name: data.contractor_name,
        contractor_company: data.contractor_company,
        responsible_party: data.responsible_party,
        has_documents: data.has_documents,
        document_count: data.document_count,
        first_document_url: data.first_document_url
      }
    },

    // Transform EPC data into certificate-like format
    transformEpc(epc) {
      const expired = epc['expired?'] || epc.expired
      const expiresSoon = epc['expires_soon?'] || epc.expires_soon
      const statusDisplay = expired ? 'Expired' : (expiresSoon ? 'Expiring Soon' : 'Active')
      return {
        id: `epc_${epc.id}`,
        property_id: epc.property_id,
        certificate_type_name: 'EPC',
        certificate_type_color: 'green',
        certificate_number: epc.certificate_number,
        expiry_date: epc.date_expires,
        formatted_expiry_date: this.formatDate(epc.date_expires),
        days_until_expiry: Math.floor((new Date(epc.date_expires) - new Date()) / (1000 * 60 * 60 * 24)),
        status_display: statusDisplay,
        status_class: this.getStatusClass(statusDisplay),
        contractor_name: null,
        energy_rating: epc.current_energy_rating,
        is_epc: true,
        epc_data: epc,
        certificate_url: epc.certificate_url,
        has_documents: !!epc.certificate_url || !!epc.has_full_certificate,
        first_document_url: epc.full_certificate_pdf_url || epc.certificate_url
      }
    },

    // Handle granular update from Action Cable (update just one row)
    handleCertificateUpdate(message) {
      switch(message.action) {
        case 'update':
          // Find and update just this row
          const index = this.certificates.findIndex(c => c.id === message.id)
          if (index !== -1) {
            const updatedCertificate = this.transformCertificate(message.data)
            this.certificates[index] = updatedCertificate
          }
          break

        case 'create':
          // New certificate for this property - add to list
          const newCertificate = this.transformCertificate(message.data)
          this.certificates.push(newCertificate)
          // Sort by expiry date descending (newest at top)
          this.certificates.sort((a, b) => new Date(b.expiry_date) - new Date(a.expiry_date))
          break

        case 'destroy':
          // Deleted certificate - remove from list
          this.certificates = this.certificates.filter(c => c.id !== message.id)
          break
      }
    },

    // Get CSS classes for status badge
    getStatusClass(statusDisplay) {
      const classMap = {
        'Active': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        'Expiring Soon': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        'Expired': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        'Retired': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
      }
      return classMap[statusDisplay] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
    },

    // Get color class for certificate type badge
    getCertificateTypeColorClass(color) {
      const colorMap = {
        'orange': 'bg-orange-500',
        'blue': 'bg-blue-500',
        'green': 'bg-green-500',
        'red': 'bg-red-500',
        'purple': 'bg-purple-500',
        'yellow': 'bg-yellow-500',
        'gray': 'bg-gray-500'
      }
      return colorMap[color] || 'bg-gray-500'
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },

    // Edit certificate - open modal with certificate data
    editCertificate(certificate) {
      window.dispatchEvent(new CustomEvent('open-certificate-modal', {
        detail: {
          mode: 'edit',
          id: certificate.id,
          property_id: certificate.property_id,
          property_address: this.getPropertyAddress(),
          certificate_type_id: certificate.certificate_type_id,
          issue_date: certificate.issue_date,
          expiry_date: certificate.expiry_date,
          certificate_number: certificate.certificate_number,
          contractor_name: certificate.contractor_name,
          contractor_company: certificate.contractor_company,
          contractor_registration_number: certificate.contractor_registration_number,
          contractor_phone: certificate.contractor_phone,
          contractor_email: certificate.contractor_email,
          contractor_id: certificate.contractor_id,
          responsible_party: certificate.responsible_party,
          external_document_url: certificate.external_document_url,
          notes: certificate.notes,
          is_epc: certificate.is_epc
        }
      }))
    },

    // Get property address for modal
    getPropertyAddress() {
      const pageTitle = document.getElementById('page-title')
      return pageTitle ? pageTitle.textContent.trim() : ''
    },

    // View documents
    viewDocuments(certificate) {
      this.$store.modals.currentCertificate = certificate
      this.$store.modals.isCertificateDocumentsModalOpen = true
    },

    // Delete certificate
    deleteCertificate(certificateId) {
      if (confirm('Are you sure you want to delete this certificate?')) {
        fetch(`/properties/${this.propertyId}/certificates/${certificateId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'application/json'
          }
        })
        .then(response => {
          if (response.ok) {
            // Remove from local array (will also be removed via Action Cable broadcast)
            this.certificates = this.certificates.filter(c => c.id !== certificateId)
          } else {
            alert('Failed to delete certificate')
          }
        })
        .catch(error => {
          console.error('Error:', error)
          alert('Failed to delete certificate')
        })
      }
    },

    // Computed property: Filter certificates based on selected filter
    get filteredCertificates() {
      if (this.filter === 'active') {
        // Show only non-expired and non-retired certificates
        return this.certificates.filter(cert => {
          return cert.status_display !== 'Expired' && cert.status_display !== 'Retired'
        })
      }
      // 'all' shows everything
      return this.certificates
    },

    // Computed property: Sort filtered certificates by expiry date
    get sortedCertificates() {
      return [...this.filteredCertificates].sort((a, b) => {
        const dateA = new Date(a.expiry_date)
        const dateB = new Date(b.expiry_date)
        return this.sortAsc ? dateA - dateB : dateB - dateA
      })
    },

    toggleSortOrder() {
      this.sortAsc = !this.sortAsc
    }
  }
}
